// One trailer's 2D packing layout as an inline SVG, rendered in React.
//
// Pallets are scaled by a SINGLE uniform px-per-ft factor (like the legacy
// report's SCALE), so they keep their true proportions. The viewBox height
// is derived from the real trailer aspect (width/length) — NOT a fixed
// value. The earlier port used separate scaleX/scaleY against a fixed
// 1040×340 viewBox, which stretched every pallet ~11% taller than reality.

import { useT } from "../lib/i18n";
import type { PackingDTO, PlacementDTO } from "../types";

// viewBox width sets the internal resolution; height is computed from the
// trailer's true aspect so scaling stays uniform on both axes.
const SVG_WIDTH = 1040;
const STROKE = "#94A3B8";
const TRAILER_FILL = "#F9FAFB";

export interface PalletAnnotations {
  colorByShipmentId: Record<string, string>;
  stopByShipmentId: Record<string, number>;
  destByShipmentId: Record<string, string>;
}

function suffixLabel(shipmentId: string): string {
  return shipmentId.includes("-")
    ? shipmentId.slice(shipmentId.lastIndexOf("-") + 1)
    : shipmentId.slice(-3);
}

function Placement({
  p,
  scale,
  color,
  stopNum,
  destZip,
}: {
  p: PlacementDTO;
  scale: number;
  color: string;
  stopNum: number | undefined;
  destZip: string | undefined;
}) {
  const t = useT();
  const px = p.x_ft * scale;
  const py = p.y_ft * scale;
  const pw = p.length_ft * scale;
  const ph = p.width_ft * scale;
  // Small labels matching the legacy report's proportions (~1.3% of trailer
  // width) — the first port clamped to 22–36px, which read far too large.
  // Derived from the legacy min(pw,ph)//3 clamped to 7–11px at 32 px/ft,
  // scaled to this renderer's ~40.8 px/ft frame.
  const fontSize = Math.max(9, Math.min(14, Math.floor(Math.min(pw, ph) / 4)));
  const stopFontSize = Math.max(8, fontSize - 3);
  const cx = px + pw / 2;
  const cy = py + ph / 2;
  return (
    <g>
      <rect
        x={px}
        y={py}
        width={pw}
        height={ph}
        fill={color}
        fillOpacity={0.22}
        stroke={color}
        strokeWidth={4}
        rx={8}
      >
        <title>
          {p.shipment_id}
          {destZip ? ` · ZIP ${destZip}` : ""}
          {stopNum !== undefined ? ` · ${t("map.stop")} ${stopNum}` : ""}
          {"\n"}
          {p.length_ft}×{p.width_ft} ft{p.rotated ? ` ${t("trailer.rotated")}` : ""}
        </title>
      </rect>
      <text
        x={cx}
        y={cy - 2}
        textAnchor="middle"
        fontSize={fontSize}
        fontWeight={600}
        fill={color}
      >
        {suffixLabel(p.shipment_id)}
      </text>
      {stopNum !== undefined && (
        <text
          x={cx}
          y={cy + fontSize}
          textAnchor="middle"
          fontSize={stopFontSize}
          fill="#475569"
        >
          S{stopNum}
        </text>
      )}
    </g>
  );
}

export function TrailerSvg({
  packing,
  annotations,
}: {
  packing: PackingDTO;
  annotations: PalletAnnotations;
}) {
  // One uniform px-per-ft scale for both axes; the trailer outline follows the
  // real aspect so a square pallet renders square.
  const scale = SVG_WIDTH / packing.trailer_length_ft;
  const trailerH = packing.trailer_width_ft * scale;
  // The simple row packer can run a pallet past the floor when a trip is tight
  // (e.g. 13 same-size pallets don't tile a clean 2-row grid). Shrink the whole
  // load uniformly so every pallet renders INSIDE the outline instead of
  // spilling out — the footprint % badge + over-capacity banner carry the
  // "how full" signal. `fit` is 1 (no shrink) whenever the load already fits.
  const rightFt = packing.placements.reduce(
    (m, p) => Math.max(m, p.x_ft + p.length_ft),
    0,
  );
  const bottomFt = packing.placements.reduce(
    (m, p) => Math.max(m, p.y_ft + p.width_ft),
    0,
  );
  const fit = Math.min(
    1,
    rightFt > 0 ? packing.trailer_length_ft / rightFt : 1,
    bottomFt > 0 ? packing.trailer_width_ft / bottomFt : 1,
  );
  return (
    <svg
      className="trailer-svg"
      viewBox={`0 0 ${SVG_WIDTH} ${trailerH}`}
      width="100%"
      preserveAspectRatio="xMidYMid meet"
    >
      <rect
        x={0}
        y={0}
        width={SVG_WIDTH}
        height={trailerH}
        fill={TRAILER_FILL}
        stroke={STROKE}
        strokeWidth={4}
        rx={10}
      />
      {packing.placements.map((p) => (
        <Placement
          key={p.shipment_id}
          p={p}
          scale={scale * fit}
          color={annotations.colorByShipmentId[p.shipment_id] ?? "#D4A017"}
          stopNum={annotations.stopByShipmentId[p.shipment_id]}
          destZip={annotations.destByShipmentId[p.shipment_id]}
        />
      ))}
      {/* Rear door indicator (x=0), drawn on top. */}
      <line x1={0} y1={0} x2={0} y2={trailerH} stroke="#1F2933" strokeWidth={10} />
    </svg>
  );
}
